import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

import { useVendorApplicationForm } from '@/hooks/useVendorApplicationForm';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const EMPLOYEE_RANGES = [
  '1-10 employees',
  '11-25 employees', 
  '26-50 employees',
  '51-100 employees',
  '101-250 employees',
  '251-500 employees',
  '500+ employees'
];

export default function PEOApply() {
  const { form, isSubmitting, isSubmitted, onSubmit } = useVendorApplicationForm();
  const { register, formState: { errors }, setValue, watch } = form;
  
  const selectedStates = watch('statesServed') || [];

  const handleStateChange = (state: string, checked: boolean) => {
    const currentStates = selectedStates;
    if (checked) {
      setValue('statesServed', [...currentStates, state]);
    } else {
      setValue('statesServed', currentStates.filter(s => s !== state));
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Application Submitted - PEO Finder Pro</title>
          <meta name="description" content="Your PEO vendor application has been successfully submitted. We'll review and contact you soon." />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Navbar />
          <main className="container mx-auto px-4 py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="mb-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-4">Application Submitted Successfully!</h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Thank you for your interest in joining our PEO network. We've received your application 
                  and will review it carefully. Our team will contact you within 3-5 business days.
                </p>
              </div>
              
              <div className="space-y-4">
                <Button asChild>
                  <Link to="/for-peos">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to For PEOs
                  </Link>
                </Button>
                <p className="text-sm text-muted-foreground">
                  Have questions? Contact us at{' '}
                  <a href="mailto:info@thepeoapp.com" className="text-primary hover:underline">
                    info@thepeoapp.com
                  </a>
                </p>
              </div>
            </motion.div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Apply to Join PEO Network - PEO Finder Pro</title>
        <meta name="description" content="Apply to join our vetted network of PEO providers. Connect with businesses seeking comprehensive HR solutions." />
        <meta name="keywords" content="PEO network, PEO provider application, join PEO network, PEO vendor" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="mb-8">
              <Link 
                to="/for-peos" 
                className="inline-flex items-center text-primary hover:underline mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to For PEOs
              </Link>
              
              <h1 className="text-3xl font-bold mb-4">Join Our PEO Network</h1>
              <p className="text-muted-foreground">
                Apply to become a vetted PEO provider in our network and connect with 
                businesses seeking comprehensive HR solutions.
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>PEO Provider Application</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={onSubmit} className="space-y-6">
                  {/* Company Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Company Information</h3>
                    
                    <div>
                      <Label htmlFor="companyName">Company Name *</Label>
                      <Input
                        id="companyName"
                        {...register('companyName')}
                        placeholder="Your PEO Company Name"
                      />
                      {errors.companyName && (
                        <p className="text-sm text-destructive mt-1">{errors.companyName.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="websiteUrl">Company Website</Label>
                      <Input
                        id="websiteUrl"
                        type="url"
                        {...register('websiteUrl')}
                        placeholder="https://yourcompany.com"
                      />
                      {errors.websiteUrl && (
                        <p className="text-sm text-destructive mt-1">{errors.websiteUrl.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Contact Information</h3>
                    
                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input
                        id="contactName"
                        {...register('contactName')}
                        placeholder="Primary contact person"
                      />
                      {errors.contactName && (
                        <p className="text-sm text-destructive mt-1">{errors.contactName.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="contactEmail">Email Address *</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        {...register('contactEmail')}
                        placeholder="contact@yourcompany.com"
                      />
                      {errors.contactEmail && (
                        <p className="text-sm text-destructive mt-1">{errors.contactEmail.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="contactPhone">Phone Number</Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        {...register('contactPhone')}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Business Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Business Details</h3>
                    
                    <div>
                      <Label>States Served *</Label>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                        {US_STATES.map((state) => (
                          <div key={state} className="flex items-center space-x-2">
                            <Checkbox
                              id={state}
                              checked={selectedStates.includes(state)}
                              onCheckedChange={(checked) => handleStateChange(state, !!checked)}
                            />
                            <Label htmlFor={state} className="text-sm">
                              {state}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {errors.statesServed && (
                        <p className="text-sm text-destructive mt-1">{errors.statesServed.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="employeeRange">Preferred Client Size</Label>
                      <Select onValueChange={(value) => setValue('employeeRange', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select employee range" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYEE_RANGES.map((range) => (
                            <SelectItem key={range} value={range}>
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Certifications & Memberships</h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="esacMember"
                          {...register('certifications.esacMember')}
                        />
                        <Label htmlFor="esacMember">ESAC (Employee Services Assurance Corporation) Member</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="irs5500Member"
                          {...register('certifications.irs5500Member')}
                        />
                        <Label htmlFor="irs5500Member">IRS 5500 Filer</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="napeoMember"
                          {...register('certifications.napeoMember')}
                        />
                        <Label htmlFor="napeoMember">NAPEO (National Association of PEOs) Member</Label>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <Label htmlFor="notes">Additional Information</Label>
                    <Textarea
                      id="notes"
                      {...register('notes')}
                      placeholder="Tell us more about your services, specialties, or anything else we should know..."
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit" 
                    className="w-full" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </main>
        
        <Footer />
      </div>
    </>
  );
}